import React, { useState } from 'react';
import { SendIcon, CheckCircle2Icon } from 'lucide-react';
import { countries } from '../data/countries';
import { WHATSAPP_NUMBER } from '../utils/whatsapp';
interface FormState {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
  message: string;
}
const initialState: FormState = {
  name: '',
  email: '',
  countryCode: 'AR',
  phone: '',
  message: ''
};
const fieldClass = 'w-full rounded-xl border border-gold/25 bg-ink px-4 py-3 text-base text-white placeholder-white/35 outline-none transition-colors duration-150 ease-out focus:border-gold focus:ring-1 focus:ring-gold';
export function ConsultationForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [sent, setSent] = useState(false);
  const country = countries.find(item => item.code === form.countryCode) ?? countries[0];
  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setSent(false);
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const phone = form.phone.replace(/\D/g, '');
    const lines = ['Hola Maestro, quiero solicitar mi consulta.', `Nombre: ${form.name}`, `Correo: ${form.email}`, `WhatsApp: +${country.dial} ${phone} (${country.name})`];
    if (form.message.trim()) lines.push(`Mi caso: ${form.message.trim()}`);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setSent(true);
  }
  return <section className="bg-ink px-5 py-16 lg:px-8 lg:py-24" id="formulario" data-id="element-13" data-mp="gxr3kec77yux">
      <div className="mx-auto max-w-2xl rounded-3xl border border-gold/25 bg-ink-soft p-6 shadow-candle sm:p-10" data-id="element-14" data-mp="bzpjbq31dthv">
        <h2 className="font-display text-2xl text-gold-soft sm:text-3xl" data-id="element-15" data-mp="q0s5s3xy15tb">
          Solicitá tu consulta
        </h2>
        <p className="mt-3 text-base text-white/65" data-id="element-16" data-mp="l28mkbaqi84n">
          Dejame tus datos y el envío abre directamente el chat de WhatsApp con
          el Maestro.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate data-id="element-17" data-mp="3jqxd5fnz68ai">
          <div data-id="element-18" data-mp="3ntk5dhok5v88">
            <label htmlFor="name" className="block text-sm font-semibold text-gold-soft" data-id="element-19" data-mp="3nt20xxrqsap8">
              Nombre
            </label>
            <input id="name" name="name" type="text" required autoComplete="name" placeholder="Tu nombre completo" value={form.name} onChange={event => update('name', event.target.value)} className={`mt-2 ${fieldClass}`} data-id="element-20" data-mp="1dpp5qpp7ida" />
          </div>

          <div data-id="element-21" data-mp="fbe8uv814jgr">
            <label htmlFor="email" className="block text-sm font-semibold text-gold-soft" data-id="element-22" data-mp="2d39wzup4e7sc">
              Correo electrónico
            </label>
            <input id="email" name="email" type="email" required autoComplete="email" placeholder="tucorreo@ejemplo.com" value={form.email} onChange={event => update('email', event.target.value)} className={`mt-2 ${fieldClass}`} data-id="element-23" data-mp="2h6oopimf8clm" />
          </div>

          <div data-id="element-24" data-mp="1vvserdcxwj7z">
            <label htmlFor="phone" className="block text-sm font-semibold text-gold-soft" data-id="element-25" data-mp="1vs4nkkuyw8wf">
              WhatsApp
            </label>
            <div className="mt-2 grid grid-cols-[minmax(120px,150px)_1fr] gap-3" data-id="element-26" data-mp="2nlwxl7l1wy73">
              <select aria-label="País" value={form.countryCode} onChange={event => update('countryCode', event.target.value)} className={`${fieldClass} appearance-none pr-3`} data-id="element-27" data-mp="2ji79skc7akn4">
                {countries.map(item => <option key={item.code} value={item.code} data-id="element-28" data-mp="133pa42rhhyn7">
                    {item.flag} +{item.dial}
                  </option>)}
              </select>
              <input id="phone" name="phone" type="tel" required inputMode="tel" autoComplete="tel" placeholder="11 6625 2465" value={form.phone} onChange={event => update('phone', event.target.value)} className={fieldClass} data-id="element-29" data-mp="2vsycf7jrxzg9" />
            </div>
            <p className="mt-2 text-xs text-white/45" data-id="element-30" data-mp="3eq9eezwsj6ze">
              Elegí tu país: {country.name} (+{country.dial})
            </p>
          </div>

          <div data-id="element-31" data-mp="35qulzux2cmc9">
            <label htmlFor="message" className="block text-sm font-semibold text-gold-soft" data-id="element-32" data-mp="35qcsh61scm81">
              Contame tu caso (opcional)
            </label>
            <textarea id="message" name="message" rows={3} placeholder="Amor, dinero, suerte, separación…" value={form.message} onChange={event => update('message', event.target.value)} className={`mt-2 resize-none ${fieldClass}`} data-id="element-33" data-mp="1uc8p599b5lqb" />
          </div>

          <button type="submit" className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-wa px-6 py-4 text-base font-semibold uppercase tracking-wide text-[#062b12] transition-[transform,background-color] duration-150 ease-out hover:bg-[#1fbb59] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-soft active:scale-[0.99]" data-id="element-34" data-mp="1vqs7jjnpmn5j">
            <SendIcon className="h-5 w-5" aria-hidden="true" data-id="element-35" data-mp="183jzwv2t720c" />
            Enviar
          </button>

          <p aria-live="polite" className="min-h-[20px] text-sm font-medium text-wa" data-id="element-36" data-mp="kd6895o4z56a">
            {sent && <span className="inline-flex items-center gap-2" data-id="element-37" data-mp="fe3bs9m8279x">
                <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-38" data-mp="2fhnqq0ec9nlk" />
                Abrimos tu chat de WhatsApp con el Maestro.
              </span>}
          </p>
        </form>
      </div>
    </section>;
}